import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authHeaders } from '../../utils/odata';
import './GlobalSearch.css';

// Sunucu da 2 karakter altını reddeder (SearchController.MinTermLength) — burada da eleyip
// gereksiz isteği hiç atmıyoruz.
const MIN_CHARS = 2;
const DEBOUNCE_MS = 250;
const RECENT_KEY = 'globalSearch.recent';
const RECENT_MAX = 6;

function SearchIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M10.9 18.1a7.2 7.2 0 1 1 0-14.4 7.2 7.2 0 0 1 0 14.4Z" stroke="currentColor" strokeWidth="1.8" />
      <path d="M20.3 20.3l-4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function loadRecents() {
  try {
    const raw = JSON.parse(localStorage.getItem(RECENT_KEY));
    return Array.isArray(raw) ? raw : [];
  } catch {
    return [];
  }
}

const amountFmt = new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' });

function formatDate(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString('tr-TR');
}

/**
 * Global arama — üst bardaki tek kutu. Ctrl/⌘+K ile odaklanır.
 * Sonuçlar /api/search'ten gelir; tenant ve kullanıcı veri yetkisi SUNUCUDA uygulanır
 * (bkz. SearchController.BuildScope) — burada güvenlik amaçlı hiçbir filtre yoktur.
 */
export function GlobalSearch() {
  const navigate = useNavigate();
  const [term, setTerm] = useState('');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);
  const [active, setActive] = useState(0);
  const [recents, setRecents] = useState(loadRecents);

  const boxRef = useRef(null);
  const inputRef = useRef(null);
  const abortRef = useRef(null);

  // Ctrl/⌘+K — sayfanın neresinde olursa olsun aramaya odaklan.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key?.toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    const onDown = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  // Debounce + arama. Kullanıcı yazmaya devam ederse uçuştaki istek iptal edilir — yoksa yavaş
  // dönen eski bir yanıt yeni sonuçların üstüne yazabilir (yarış durumu).
  useEffect(() => {
    const q = term.trim();
    if (q.length < MIN_CHARS) {
      abortRef.current?.abort();
      setItems([]);
      setLoading(false);
      setError(null);
      return undefined;
    }

    setLoading(true);
    const timer = setTimeout(async () => {
      abortRef.current?.abort();
      const ac = new AbortController();
      abortRef.current = ac;

      try {
        const res = await fetch(`/api/search?q=${encodeURIComponent(q)}`, {
          headers: authHeaders(),
          signal: ac.signal,
        });
        if (!res.ok) throw new Error(String(res.status));
        const data = await res.json();
        setItems(data?.items ?? []);
        setError(null);
        setActive(0);
      } catch (err) {
        if (err.name === 'AbortError') return;
        setItems([]);
        setError('Arama yapılamadı.');
      } finally {
        // Yalnız GÜNCEL istek spinner'ı kapatsın: iptal edilen eski bir istek, yerine geçen
        // yeni isteğin spinner'ını söndürmemeli.
        if (abortRef.current === ac) setLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [term]);

  const groups = useMemo(() => {
    const map = new Map();
    for (const item of items) {
      if (!map.has(item.type)) map.set(item.type, { type: item.type, label: item.typeLabel, rows: [] });
      map.get(item.type).rows.push(item);
    }
    return [...map.values()];
  }, [items]);

  // Klavye gezinmesi grup sınırlarını görmez — düz sıra üzerinden yürür.
  const flat = useMemo(() => groups.flatMap((g) => g.rows), [groups]);

  const go = useCallback(
    (item) => {
      if (!item) return;
      const next = [item, ...loadRecents().filter((r) => r.id !== item.id)].slice(0, RECENT_MAX);
      localStorage.setItem(RECENT_KEY, JSON.stringify(next));
      setRecents(next);

      setOpen(false);
      setTerm('');
      setItems([]);
      inputRef.current?.blur();
      navigate(item.route);
    },
    [navigate],
  );

  const showRecents = term.trim().length < MIN_CHARS;
  const visible = showRecents ? recents : flat;

  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      setOpen(false);
      inputRef.current?.blur();
      return;
    }
    if (!open) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => (visible.length ? (i + 1) % visible.length : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => (visible.length ? (i - 1 + visible.length) % visible.length : 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      go(visible[active]);
    }
  };

  const renderRow = (item, index) => (
    <button
      key={`${item.type}-${item.id}`}
      type="button"
      className={`gs-row${index === active ? ' is-active' : ''}`}
      // Panel içindeki tıklama, blur/mousedown ile paneli kapatmadan önce seçimi yapabilsin.
      onMouseDown={(e) => e.preventDefault()}
      onMouseEnter={() => setActive(index)}
      onClick={() => go(item)}
    >
      <span className="gs-row-main">
        <span className="gs-row-title">{item.title}</span>
        {item.subtitle && <span className="gs-row-sub">{item.subtitle}</span>}
      </span>
      <span className="gs-row-meta">
        {item.badge && <span className="gs-badge">{item.badge}</span>}
        {item.amount != null && <span className="gs-amount">{amountFmt.format(item.amount)}</span>}
        {formatDate(item.date) && <span className="gs-date">{formatDate(item.date)}</span>}
      </span>
    </button>
  );

  let flatIndex = -1;

  return (
    <div className="gs-wrap" ref={boxRef}>
      <label className="ui-topbar-search">
        <SearchIcon />
        <input
          ref={inputRef}
          type="search"
          value={term}
          placeholder="Müşteri, fatura, ürün ara…"
          aria-label="Global arama"
          onChange={(e) => setTerm(e.target.value)}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
        />
        <kbd className="gs-kbd">Ctrl K</kbd>
      </label>

      {open && (
        <div className="gs-panel" role="listbox">
          {showRecents ? (
            recents.length > 0 ? (
              <>
                <div className="gs-group-title">Son bakılanlar</div>
                {recents.map((item, i) => renderRow(item, i))}
              </>
            ) : (
              <div className="gs-empty">Müşteri adı/kodu, fatura veya belge numarası, ürün… yazın.</div>
            )
          ) : (
            <>
              {loading && <div className="gs-empty">Aranıyor…</div>}
              {!loading && error && <div className="gs-empty gs-error">{error}</div>}
              {!loading && !error && flat.length === 0 && (
                <div className="gs-empty">“{term.trim()}” için sonuç yok.</div>
              )}
              {!loading &&
                !error &&
                groups.map((g) => (
                  <div key={g.type} className="gs-group">
                    <div className="gs-group-title">{g.label}</div>
                    {g.rows.map((item) => {
                      flatIndex += 1;
                      return renderRow(item, flatIndex);
                    })}
                  </div>
                ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default GlobalSearch;
